import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CreditText,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  EmailText,
  HeaderContainer,
  HeaderRightSide,
  HeaderWrapper,
  PauseIcon,
  PlayIcon,
  ProfileSection,
  ProUpgradeButtonContainer,
  ProUpgradeButtonSkeleton,
  Row,
} from "./Header.styles";
import { logout } from "../../store/slices/auth/asyncThunks";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { selectUser } from "../../store/slices/user/slice";
import {
  selectAutoPlay,
  selectSignUpModalOpen,
  setAffiliateRef,
  setAutoPlay,
  setSignUpModalOpen,
} from "../../store/slices/common/slice";
import { ReactComponent as LogoSvg } from "../../assets/icons/fullLogo.svg";
import { ReactComponent as StarSvg } from "../../assets/icons/star.svg";
import { ReactComponent as WhiteStarSvg } from "../../assets/icons/whiteStarOutline.svg";
import { ReactComponent as UserSvg } from "../../assets/icons/user.svg";
import { ReactComponent as LogoutSvg } from "../../assets/icons/logout.svg";
import { ReactComponent as ParticlesSvg } from "../../assets/icons/whiteParticles.svg";
import { ReactComponent as RewardsSvg } from "../../assets/icons/award.svg";
// import { ReactComponent as WalletSvg } from "../../assets/icons/wallet.svg";

import { ReactComponent as EditSvg } from "../../assets/icons/edit.svg";
import { ReactComponent as AffiliateSvg } from "../../assets/icons/affiliate.svg";
import AuthModal from "../AuthModal/AuthModal";
import FreeCreditsModal from "../FreeCreditsModal/FreeCreditsModal";
import UsernameModal from "../UsernameModal/UsernameModal";
import { priceFormatter } from "../../utils/utils";
import { toast } from "sonner";
import {
  clearExploreCollections,
  selectCategories,
  setSelectedExploreCollectionFilter,
} from "../../store/slices/model/slice";

const Header = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDropdownAnimating, setIsDropdownAnimating] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isUsernameModalOpen, setIsUsernameModalOpen] = useState(false);
  const [isFreeCreditsModalOpen, setIsFreeCreditsModalOpen] = useState(false);
  const navigate = useNavigate();
  const loggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);
  const dropdownRef = useRef(null);
  const signUpModalOpen = useSelector(selectSignUpModalOpen);
  const autoplayEnabled = useSelector(selectAutoPlay);
  const { data: categories } = useSelector(selectCategories);

  const handleAutoplayToggle = () => {
    localStorage.setItem("autoPlay", !autoplayEnabled);
    dispatch(setAutoPlay(!autoplayEnabled));
  };

  const closeDropdown = () => {
    setIsDropdownAnimating(true);
    setTimeout(() => {
      setIsDropdownOpen(false);
      setIsDropdownAnimating(false);
    }, 150);
  };

  useEffect(() => {
    if (user) {
      if (user.username.includes("-")) {
        setIsUsernameModalOpen(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (signUpModalOpen) {
      setIsLoginModalOpen(true);
    }
  }, [signUpModalOpen]);

  useEffect(() => {
    if (searchParams.get("code")) {
      setIsLoginModalOpen(true);
    }
    const ref = searchParams.get("ref");
    if (ref) {
      if (loggedIn) {
        searchParams.delete("ref");
        setSearchParams(searchParams);
      } else {
        dispatch(setAffiliateRef(ref));
        setIsLoginModalOpen(true);
      }
    }
  }, [searchParams, dispatch, loggedIn, setSearchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => {
    if (loggedIn) {
      if (isDropdownOpen) {
        closeDropdown();
      } else {
        setIsDropdownOpen(!isDropdownOpen);
      }
    } else {
      setIsLoginModalOpen(!isLoginModalOpen);
    }
  };

  const handleNavigate = (path) => {
    navigate(`/${path}`);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };
  const handleUpgradeToPro = () => {
    if (loggedIn) {
      handleNavigate("subscription");
    } else {
      setIsLoginModalOpen(!isLoginModalOpen);
    }
  };

  const handleDropdownItemClick = (action) => {
    if (action === "autoPlay") {
      handleAutoplayToggle();
    } else if (action === "subscriptions") {
      handleNavigate("subscription");
    } else if (action === "freeCredits") {
      setIsFreeCreditsModalOpen(true);
    } else if (action === "deleteAccount") {
      handleNavigate("account/delete");
    } else if (action === "logout") {
      toast.success("Successfully logged out!");
      dispatch(logout());
    } else if (action === "templateRewards") {
      handleNavigate("rewards/template");
    } else if (action === "affiliate") {
      handleNavigate("referral/earnings");
    } else if (action === "wallet") {
      handleNavigate("wallet");
    }
    closeDropdown();
  };

  const handleFreeCreditsModalClose = () => {
    setIsFreeCreditsModalOpen(false);
  };

  return (
    <HeaderContainer>
      <HeaderWrapper>
        <LogoSvg
          onClick={() => {
            dispatch(clearExploreCollections());
            dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
            navigate("/");
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 300);
          }}
        />
        <HeaderRightSide>
          {user !== null ? (
            <ProUpgradeButtonContainer onClick={handleUpgradeToPro}>
              <StarSvg />
              <CreditText>
                {user !== undefined ? priceFormatter(user.credits) : 0}
              </CreditText>
            </ProUpgradeButtonContainer>
          ) : (
            <ProUpgradeButtonSkeleton $animationTime={5} />
          )}

          <ProfileSection ref={dropdownRef}>
            <UserSvg onClick={toggleDropdown} style={{ cursor: "pointer" }} />

            {isDropdownOpen && (
              <DropdownMenu $animating={isDropdownAnimating}>
                <DropdownItem
                  onClick={() => handleDropdownItemClick("subscriptions")}
                >
                  Subscription <WhiteStarSvg width={18} height={18} />
                </DropdownItem>
                <DropdownItem
                  onClick={() => handleDropdownItemClick("freeCredits")}
                >
                  Free Credits <ParticlesSvg width={18} height={18} />
                </DropdownItem>
                <DropdownItem
                  onClick={() => handleDropdownItemClick("affiliate")}
                >
                  Affiliate <AffiliateSvg width={18} height={18} />
                </DropdownItem>
                <DropdownItem
                  onClick={() => handleDropdownItemClick("templateRewards")}
                >
                  Template Rewards <RewardsSvg width={18} height={18} />
                </DropdownItem>

                {/* <DropdownItem onClick={() => handleDropdownItemClick("wallet")}>
                  Wallet <WalletSvg width={18} height={18} />
                </DropdownItem> */}

                <DropdownSeparator />

                <DropdownItem
                  onClick={() => handleDropdownItemClick("autoPlay")}
                >
                  AutoPlay
                  {autoplayEnabled ? <PlayIcon /> : <PauseIcon />}
                </DropdownItem>
                <DropdownItem onClick={() => handleDropdownItemClick("logout")}>
                  Logout <LogoutSvg width={18} height={18} />
                </DropdownItem>
                {/* <DropdownItem
                  onClick={() => handleDropdownItemClick("deleteAccount")}
                >
                  Delete Account{" "}
                  <DeleteSvg
                    width={18}
                    height={18}
                    style={{ marginRight: 3 }}
                  />
                </DropdownItem> */}

                <DropdownSeparator />
                <Row>
                  <EmailText>{user?.username}</EmailText>
                  <EditSvg
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setIsUsernameModalOpen(true);
                    }}
                  />
                </Row>
              </DropdownMenu>
            )}
          </ProfileSection>
        </HeaderRightSide>

        <FreeCreditsModal
          isOpen={isFreeCreditsModalOpen}
          onClose={handleFreeCreditsModalClose}
        />

        <AuthModal
          isOpen={isLoginModalOpen}
          onClose={() => {
            dispatch(setSignUpModalOpen(false));
            setIsLoginModalOpen(false);
          }}
        />

        <UsernameModal
          isOpen={isUsernameModalOpen}
          onClose={() => setIsUsernameModalOpen(false)}
        />
      </HeaderWrapper>
    </HeaderContainer>
  );
};

export default Header;
